/**
 * Free Gift Offer Handler
 * Handles adding free gift to cart from cart drawer/page
 * Uses event delegation to work with dynamically updated DOM
 *
 * This script is loaded once and handles all free gift button clicks
 * even when the cart DOM is updated via morphing/AJAX
 */

class FreeGiftHandler {
  constructor() {
    this.isProcessing = false;
    this.processingButtons = new WeakSet(); // Track processing buttons
    this.init();
  }

  init() {
    // Use event delegation on document level with capture phase
    // This ensures the handler works even when DOM is replaced
    document.addEventListener('click', this.handleClick.bind(this), true);
    console.log('[FreeGift] Handler initialized and ready');
  }

  handleClick(event) {
    // Find the free gift button using closest() for better delegation
    const button = event.target.closest('[data-free-gift-add]');

    if (!button) {
      return; // Not our button, ignore
    }

    // Prevent any default behavior immediately
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    console.log('[FreeGift] Button clicked, checking state...');

    // Multi-level processing check for maximum reliability
    if (this.isProcessing) {
      console.log('[FreeGift] Global processing lock active, ignoring click');
      return;
    }

    if (this.processingButtons.has(button)) {
      console.log('[FreeGift] This specific button is already processing, ignoring click');
      return;
    }

    if (button.disabled || button.dataset.processing === 'true') {
      console.log('[FreeGift] Button is disabled or marked as processing, ignoring click');
      return;
    }

    // Get and validate variant ID
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId || isNaN(variantId)) {
      console.error('[FreeGift] Invalid variant ID:', button.dataset.variantId);
      return;
    }

    console.log('[FreeGift] All checks passed, proceeding to add to cart');
    this.addToCart(button, variantId);
  }

  async addToCart(button, variantId) {
    // Set all processing locks
    this.isProcessing = true;
    this.processingButtons.add(button);

    const originalText = button.textContent;

    // Update button UI immediately
    button.dataset.processing = 'true';
    button.disabled = true;
    button.textContent = 'מוסיף...';

    console.log('[FreeGift] Starting add to cart request for variant:', variantId);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true'
            }
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
      }

      const data = await response.json();
      console.log('[FreeGift] Successfully added to cart:', data);

      // Hide offer card with smooth animation
      const offerCard = button.closest('[data-cart-free-gift]');
      if (offerCard) {
        offerCard.style.opacity = '0';
        offerCard.style.transform = 'translateY(-10px)';

        setTimeout(() => {
          offerCard.style.display = 'none';
        }, 300);
      }

      // Set flag to reopen cart drawer after reload
      sessionStorage.setItem('freeGiftAdded', 'true');
      console.log('[FreeGift] Set sessionStorage flag to reopen drawer after reload');

      // Reload page to update cart display
      setTimeout(() => {
        console.log('[FreeGift] Reloading page to update cart');
        window.location.reload();
      }, 400);

    } catch (error) {
      console.error('[FreeGift] Error adding to cart:', error);

      // Reset all processing states
      this.isProcessing = false;
      this.processingButtons.delete(button);
      button.dataset.processing = 'false';
      button.disabled = false;
      button.textContent = originalText;

      // Show user-friendly error message
      alert('שגיאה בהוספת המתנה: ' + error.message);
    }
  }
}

// Initialize only once
if (!window.freeGiftHandler) {
  window.freeGiftHandler = new FreeGiftHandler();
}

